<script setup lang="ts">
import { getCoverImageUrl } from './helpers';
import { type FragmentType, graphql } from '@/gql';
import { format, parseISO } from 'date-fns';
import jaLocale from 'date-fns/esm/locale/ja';
import { generateRandomDateFrom12To72HoursForTimerex } from 'theme/utils/generateRandomDateFrom12To72HoursForTimerex';
import { SeminarApplicationStatus } from '~/gql/graphql';
import IconCalendar from '~theme/assets/icons/outline/calendar.svg?component';
import PillShapedSpan from '~theme/components/atoms/PillShapedSpan.vue';

const SeminarCardFragment = graphql(`
  fragment SeminarCardV2Seminar on Seminar {
    id
    title
    applicantsDescription
    applicationFee
    coverImage
    timerexUrl: timerexURL
    speakers {
      id
      name
      iconPath
    }
    schedules {
      startedAt
      endedAt
      applicationStatus
    }
    categories
  }
`);

const props = defineProps<{
  seminar: FragmentType<typeof SeminarCardFragment>;
}>();

const seminar = useComputedFragment(SeminarCardFragment, () => props.seminar);

const { buildImgixURL } = useImgixURL();

const coverImageUrl = computed(() =>
  seminar.value.coverImage
    ? buildImgixURL(seminar.value.coverImage, {
        width: 800,
      })
    : getCoverImageUrl(seminar.value.id)
);

// TODO: timerexのsuperfrog側変更したらやる
const activeSchedules = computed(() =>
  seminar.value.schedules?.filter(
    ({ applicationStatus }) =>
      applicationStatus === SeminarApplicationStatus.Applicable
  )
);

const firstScheduleWillStartAt = computed(() => {
  if (seminar.value?.timerexUrl) {
    const now = new Date();
    const key = `${now.getFullYear()}${now.getMonth()}${now.getDate()}${now.getHours()}${seminar.value.id}`;
    return generateRandomDateFrom12To72HoursForTimerex(parseInt(key));
  }

  const schedule = activeSchedules.value?.[0]?.startedAt;
  return schedule ? parseISO(schedule) : undefined;
});

const date = (d: NonNullable<(typeof firstScheduleWillStartAt)['value']>) =>
  `${d.getMonth() + 1}/${d.getDate()}`;
const dayOfWeek = (
  d: NonNullable<(typeof firstScheduleWillStartAt)['value']>
) => format(d, 'eeee', { locale: jaLocale });
const time = (d: NonNullable<(typeof firstScheduleWillStartAt)['value']>) =>
  format(d, 'H:mm~', { locale: jaLocale });

const { toLabel } = useSeminarCategories();
</script>

<template>
  <NuxtLink :class="$style.card" :href="`/seminar/${seminar.id}`">
    <img :class="$style.card__cover" :src="coverImageUrl" />
    <div :class="$style.card__text">
      <div v-if="firstScheduleWillStartAt" :class="$style.description">
        <span :class="$style.description__date">{{
          date(firstScheduleWillStartAt)
        }}</span
        ><span :class="$style.description__dayOfWeek">{{
          dayOfWeek(firstScheduleWillStartAt)
        }}</span
        ><span :class="$style.description__time">{{
          time(firstScheduleWillStartAt)
        }}</span
        ><span
          v-if="seminar.timerexUrl"
          :class="$style.description__otherSchedules"
          ><IconCalendar
            :class="$style.description__calendarIcon"
          />他の選択可能な日程があります</span
        ><span
          v-else-if="activeSchedules.length > 1"
          :class="$style.description__otherSchedules"
          ><IconCalendar
            :class="$style.description__calendarIcon"
          />他の日程が{{ activeSchedules.length - 1 }}件あります</span
        >
      </div>
      <div :class="$style.card__title">
        {{ seminar.title }}
      </div>
      <div :class="$style.tags">
        <PillShapedSpan
          v-for="c in seminar.categories"
          :key="`${seminar.id}:${c}`"
          :class="$style.tags__tag"
          >{{ toLabel(c) }}</PillShapedSpan
        >
      </div>
    </div>
  </NuxtLink>
</template>

<style module lang="scss">
.card {
  color: unset;
  text-decoration: unset;
  display: block;
  &__cover {
    display: block;
    border-radius: 8px;
    object-fit: cover;
    aspect-ratio: 2 / 1;
    width: 100%;
  }

  &__text {
    padding: 8px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__title {
    line-height: 1.8;
    font-size: 20px;
    font-weight: bold;

    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.description {
  display: flex;
  align-items: center;

  &__date {
    line-height: 1;
    color: $color-text-and-object-accent-primary;
    font-size: 24px;
    font-weight: bold;
  }
  &__dayOfWeek {
    margin-left: 4px;
    font-size: 12px;
    font-weight: bold;
  }
  &__time {
    font-size: 12px;
    font-weight: bold;
  }
  &__otherSchedules {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: $color-text-and-object-medium-emphasis;
    text-align: center;
  }
  &__calendarIcon {
    margin-left: 8px;
    width: 16px;
    height: 16px;
    fill: $color-text-and-object-medium-emphasis;
  }
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  &__tag {
    background-color: $color-surface-secondary;
    border: 2px solid $color-border-low-emphasis;
  }
}
</style>
